import { useState } from 'react';
import { localStorageGet, localStorageSet } from 'utils/localStorage';
import { ConsistencyRuleView } from '../types';
import { RuleSort, RuleSortColumn, sortConsistencyRules } from '../helpers';

export const RULE_LIST_SORT_STORAGE_KEY = 'dataConsistency:ruleList:sort';

const isSortableColumn = (value: unknown): value is RuleSortColumn =>
  value === 'customerDefinedId' ||
  value === 'name' ||
  value === 'operator' ||
  value === 'severity';

const isSortDirection = (value: unknown): value is 'asc' | 'desc' =>
  value === 'asc' || value === 'desc';

const isValidStoredSort = (value: unknown): value is RuleSort => {
  if (value === null || typeof value !== 'object') return false;
  if (!('column' in value) || !('direction' in value)) return false;
  const { column, direction } = value;
  return isSortableColumn(column) && isSortDirection(direction);
};

const hydrateSort = (): RuleSort | null => {
  const raw = localStorageGet<unknown>(RULE_LIST_SORT_STORAGE_KEY);
  return isValidStoredSort(raw) ? raw : null;
};

const nextDirection = (
  current: RuleSort | null,
  column: RuleSortColumn
): RuleSort | null => {
  if (current === null || current.column !== column) {
    return { column, direction: 'asc' };
  }
  if (current.direction === 'asc') {
    return { column, direction: 'desc' };
  }
  return null;
};

export const useComparisonRuleListSort = (rules: ConsistencyRuleView[]) => {
  const [sort, setSort] = useState<RuleSort | null>(hydrateSort);

  const handleSort = (column: RuleSortColumn) => {
    const next = nextDirection(sort, column);
    setSort(next);
    localStorageSet(RULE_LIST_SORT_STORAGE_KEY, next);
  };

  const displayedRules = sortConsistencyRules(rules, sort);

  return { sort, handleSort, displayedRules };
};
